import { localDateTime } from '../../../lib/localDateTime'
import type { DayOfLeadDto, ScheduleAssignmentDto, ScheduleMeetingDto } from '../../../types/lobbyDay'
import { formatSlotTime } from '../scheduler/lobbyScheduleUtils'

/** A group below this many non-absent members is flagged short-handed (DB-6). */
export const MIN_WORKABLE_GROUP_SIZE = 2

export type MeetingPhase = 'upcoming' | 'inProgress' | 'done' | 'reportOwed' | 'cancelled'

/** DB-4: the organizer outcome or any attendee self-report clears "report owed". */
export function reportReceived(m: ScheduleMeetingDto): boolean {
  return m.askResponse !== 'NotRecorded' || m.attendeeReportCount > 0
}

/** DB-3: derived from local wall-clock time. At start = in progress; at end = done/owed. */
export function meetingPhase(m: ScheduleMeetingDto, now: Date): MeetingPhase {
  if (m.status === 'Cancelled') return 'cancelled'
  const t = now.getTime()
  if (t < localDateTime(m.meetingDate, m.startTime).getTime()) return 'upcoming'
  if (t < localDateTime(m.meetingDate, m.endTime).getTime()) return 'inProgress'
  if (m.status === 'DropOff') return 'done'
  return reportReceived(m) ? 'done' : 'reportOwed'
}

export interface GroupStrength {
  assigned: number
  present: number
  late: number
  absent: number
  shortHanded: boolean
  lostConstituent: boolean
}

/** DB-6: Late counts as present; Absent does not. Cancelled / DropOff are never flagged. */
export function groupStrength(m: ScheduleMeetingDto): GroupStrength {
  const a = m.assignments
  const absent = a.filter(x => x.attendance === 'Absent').length
  const late = a.filter(x => x.attendance === 'Late').length
  const present = a.length - absent
  const flaggable = m.status !== 'Cancelled' && m.status !== 'DropOff'
  const constituents = a.filter(x => x.constituency === 'Constituent')
  return {
    assigned: a.length,
    present,
    late,
    absent,
    shortHanded: flaggable && present < MIN_WORKABLE_GROUP_SIZE,
    lostConstituent:
      flaggable && constituents.length > 0 && constituents.every(x => x.attendance === 'Absent'),
  }
}

export function leadOf(m: ScheduleMeetingDto): ScheduleAssignmentDto | null {
  return m.assignments.find(x => x.role === 'Lead') ?? null
}

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** DB-18: today (local) when within the event, else the start date. */
export function boardDate(startDate: string, endDate: string | null, now: Date): string {
  const today = ymd(now)
  return today >= startDate && today <= (endDate ?? startDate) ? today : startDate
}

export interface BoardCard {
  meeting: ScheduleMeetingDto
  phase: MeetingPhase
  strength: GroupStrength
  lead: ScheduleAssignmentDto | null
}

const byStart = (a: BoardCard, b: BoardCard) => a.meeting.startTime.localeCompare(b.meeting.startTime)
const byEnd = (a: BoardCard, b: BoardCard) => a.meeting.endTime.localeCompare(b.meeting.endTime)

export function buildBoard(meetings: ScheduleMeetingDto[], date: string, now: Date) {
  const cards: BoardCard[] = meetings
    .filter(m => m.meetingDate === date)
    .map(m => ({ meeting: m, phase: meetingPhase(m, now), strength: groupStrength(m), lead: leadOf(m) }))
  const pick = (p: MeetingPhase) => cards.filter(c => c.phase === p)
  return {
    now: pick('inProgress').sort(byStart),
    next: pick('upcoming').sort(byStart),
    owed: pick('reportOwed').sort(byEnd),
    done: pick('done').sort((a, b) => byEnd(b, a)),
    cancelled: pick('cancelled').sort(byStart),
  }
}

/** `tel:` link with formatting stripped (leading + kept); null when fewer than 7 digits. */
export function telHref(phone: string | null): string | null {
  if (!phone) return null
  const cleaned = phone.replace(/[^\d+]/g, '')
  return cleaned.replace(/\D/g, '').length < 7 ? null : `tel:${cleaned}`
}

export function smsNudgeHref(lead: DayOfLeadDto, legislatorName: string, startTime: string): string | null {
  const tel = telHref(lead.phone)
  if (!tel) return null
  const body =
    `Hi ${lead.firstName} — when you're out of the ${formatSlotTime(startTime)} meeting with ${legislatorName}, ` +
    `please send me the report-back: Yes / No / Maybe, plus anything they said. Thanks!`
  return `sms:${tel.slice('tel:'.length)}?&body=${encodeURIComponent(body)}`
}
